import { isAxiosError } from 'axios';
import axios from '|/helpers/axios';
import { BANK_ACCOUNT_ENDPOINTS, CARD_ENDPOINTS, PAYMENT_METHOD_ENDPOINTS } from './endpoints';
import {
  createStripeSetupIntentResponseSchema,
  bankAccountSchema,
  cardSchema,
  addAccountErrorResponseSchema,
  plaidLinkTokenResponseSchema,
  type AddBankAccountRequest,
  type AddBankAccountFromPlaidRequest,
  type AddCardFromStripeRequest,
  type SelectBankAccountRequest,
  type SelectCardRequest,
  type SelectDigitalWalletRequest,
  paymentMethodSummarySchema
} from './types';

const transformAddAccountError = async (error: unknown) => {
  const displayErrorMessage = isAxiosError(error)
    ? await addAccountErrorResponseSchema
        .validate(error.response?.data)
        .then(errorResponse => errorResponse.customerErrorMessage)
        .catch(() => undefined)
    : undefined;

  return { hasError: true, displayErrorMessage } as const;
};

export const getPaymentMethodSummary = async (signal?: AbortSignal) =>
  axios
    .get(PAYMENT_METHOD_ENDPOINTS.GET_PAYMENT_METHOD_SUMMARY, { signal })
    .then(response => response.data as unknown)
    .then(responseData => paymentMethodSummarySchema.validate(responseData));

export const createStripeSetupIntent = async () =>
  axios
    .post(CARD_ENDPOINTS.CREATE_STRIPE_SETUP_INTENT)
    .then(response => response.data as unknown)
    .then(responseData => createStripeSetupIntentResponseSchema.validate(responseData));

export const selectBankAccount = async (request: SelectBankAccountRequest) =>
  axios.post(PAYMENT_METHOD_ENDPOINTS.SELECT_BANK_ACCOUNT, request).then(() => undefined);

export const selectCard = async (request: SelectCardRequest) =>
  axios.post(PAYMENT_METHOD_ENDPOINTS.SELECT_CARD, request).then(() => undefined);

export const selectDigitalWallet = async (request?: SelectDigitalWalletRequest) =>
  axios.post(PAYMENT_METHOD_ENDPOINTS.SELECT_DIGITAL_WALLET, request).then(() => undefined);

export const getPlaidLinkToken = async () =>
  axios
    .post(BANK_ACCOUNT_ENDPOINTS.CREATE_PLAID_LINK_TOKEN)
    .then(response => response.data as unknown)
    .then(responseData => plaidLinkTokenResponseSchema.validate(responseData));

export const addBankAccount = async (request: AddBankAccountRequest) =>
  axios
    .post(BANK_ACCOUNT_ENDPOINTS.ADD_BANK_ACCOUNT, request)
    .then(response => response.data as unknown)
    .then(responseData => bankAccountSchema.validate(responseData))
    .then(bankAccount => ({ hasError: false, ...bankAccount }) as const)
    .catch(transformAddAccountError);

export const addBankAccountFromPlaid = async (request: AddBankAccountFromPlaidRequest) =>
  axios
    .post(BANK_ACCOUNT_ENDPOINTS.ADD_BANK_ACCOUNT_FROM_PLAID, request)
    .then(response => response.data as unknown)
    .then(responseData => bankAccountSchema.validate(responseData))
    .then(bankAccount => ({ hasError: false, ...bankAccount }) as const)
    .catch(transformAddAccountError);

export const addCardFromStripe = async (request: AddCardFromStripeRequest) =>
  axios
    .post(CARD_ENDPOINTS.ADD_CARD_FROM_STRIPE, request)
    .then(response => response.data as unknown)
    .then(responseData => cardSchema.validate(responseData))
    .then(card => ({ hasError: false, ...card }) as const)
    .catch(transformAddAccountError);
